import { SectionPaper } from "@/components/Paper";
import { SectionTitle } from "@/components/Title";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { Label } from "@/components/Label";
import {
  UserConfigGetAPI,
  UserConfigUpdateAPI,
} from "@/features/accountSettings/api/UserConfigAPI";
import { ButtonVariantOption, SubmitButton } from "@/components/Button";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  type UserConfigFormData,
  UserConfigFormDataSchema,
  type UserConfigAPIResponse,
  type UserConfigUpdateRequest,
} from "@/features/accountSettings/types/schemas";
import { GetUserMapConfigAPI } from "@/features/map/api/getUserMapConfigAPI";
import { useMapServiceContract } from "@/features/map/hooks/useMapServiceContract";
import {
  INITIAL_MAP_LOCATIONS,
  DEFAULT_MAP_LOCATION,
  findLocationByCoords,
  type MapLocation,
} from "@/utils/mapLocations";
import Tooltip from "@mui/material/Tooltip";
import HelpIcon from "@mui/icons-material/Help";
import { useMapServicePermission } from "@/hooks/useMapServicePermission";
import { SavedMapPlotRequestDetailAdditionalFieldsConfig } from "@/features/accountSettings/components/SavedMapPlotRequestDetailAdditionalFieldsConfig";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useSWRConfig } from "swr";
import { type NewAdditionalFieldItem } from "@/features/accountSettings/types/additionalFields";
import TextField from "@mui/material/TextField";
import { PAST_DAYS_MIN, PAST_DAYS_MAX } from "@/utils/pastDaysValidation";

// 各種設定内のサブセクション見出し（不動産登記／地図／地図・物件リスト）
const SubSectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    sx={{
      fontSize: "16px",
      fontWeight: 700,
      color: "primary.main",
      borderBottom: 1,
      borderColor: "border.main",
      pb: 0.5,
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

export const UserConfig: React.FC = (_props) => {
  const userConfig = UserConfigGetAPI();
  const { data: mapConfig } = GetUserMapConfigAPI();
  const { trigger } = UserConfigUpdateAPI();
  const { mutate } = useSWRConfig();
  const [hasMapPermission] = useMapServicePermission();
  const shouldShowMapChibanSettings = hasMapPermission === true;
  // 地番(TBN)設定はゼンリン地番検索専用のため、ゼンリン契約がある場合のみ表示する
  const { data: mapServiceContract } = useMapServiceContract();
  const hasZenrinContract = mapServiceContract?.hasZenrinContract === true;
  const { crm } = useFeatureFlags();

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const reactHookForm = useForm<UserConfigFormData>({
    mode: "all",
    defaultValues: {
      pictureRequestPastDays: 0,
      mapInitLat: DEFAULT_MAP_LOCATION.lat,
      mapInitLon: DEFAULT_MAP_LOCATION.lon,
      mapChibanIsMatch: false,
      mapZenrinChibanIncludesTbn: true,
      mapPlotRequestDetailAdditionalFields: { fields: [] },
    },
    resolver: zodResolver(UserConfigFormDataSchema),
  });

  // 現在のフォームの座標の値から駅名を取得（該当なしならundefined）
  const convertCoordinatesToStationName = (): string | undefined => {
    const lat = reactHookForm.watch("mapInitLat");
    const lon = reactHookForm.watch("mapInitLon");
    const station = findLocationByCoords(lat, lon);
    return station?.name;
  };

  useEffect(() => {
    // テキストボックスに何か入力してしばらく放置すると、再びサーバーからデータを取りに行った時に入力中の値が上書きされてしまう
    // そこで、一度でも入力があったら更新しないようにする
    if (reactHookForm.formState.isDirty) {
      return;
    }

    // userConfigとmapConfigの両方が揃うまで待つ
    if (!userConfig || !mapConfig) {
      return;
    }

    // reset()を使って全フォーム値を一度に初期化
    reactHookForm.reset({
      pictureRequestPastDays: userConfig.pictureRequestPastDays ?? 0,
      mapChibanIsMatch: userConfig.mapChibanIsMatch ?? false,
      mapZenrinChibanIncludesTbn: userConfig.mapZenrinChibanIncludesTbn ?? true,
      mapPlotRequestDetailAdditionalFields:
        userConfig.mapPlotRequestDetailAdditionalFields ?? { fields: [] },
      mapInitLat: mapConfig?.mapInitLat ?? DEFAULT_MAP_LOCATION.lat,
      mapInitLon: mapConfig?.mapInitLon ?? DEFAULT_MAP_LOCATION.lon,
    });
  }, [userConfig, mapConfig]);

  const onSubmit = async (): Promise<void> => {
    // 手動でバリデーションを実行
    const isValid = await reactHookForm.trigger();
    if (!isValid) {
      return;
    }

    // バリデーションに成功したらサーバーへ送信する
    const formData = reactHookForm.getValues();

    // 送信用データを作成（新規項目はkey/fieldTypeのみ、既存項目はすべてのフィールド）
    const requestData: UserConfigUpdateRequest = {
      ...formData,
    };

    // 新規項目（id=""）の場合、key/fieldTypeのみを送信（バックエンドでUUID等を自動生成）
    if (formData.mapPlotRequestDetailAdditionalFields?.fields) {
      requestData.mapPlotRequestDetailAdditionalFields = {
        fields: formData.mapPlotRequestDetailAdditionalFields.fields.map(
          (field) => {
            if (field.id === "") {
              const newField: NewAdditionalFieldItem = {
                key: field.key,
                fieldType: field.fieldType,
              };
              return newField;
            }
            return field;
          }
        ),
      };
    }

    try {
      const result = await trigger(requestData);
      // SWRキャッシュを再検証してサーバーから最新データを取得
      const freshData = await mutate<UserConfigAPIResponse>("/user/config");

      // 保存後、フォームを最新のバックエンドデータでリセット（UUIDなどが生成されたデータを反映）
      if (freshData && result) {
        reactHookForm.reset({
          pictureRequestPastDays: freshData.pictureRequestPastDays ?? 0,
          mapChibanIsMatch: freshData.mapChibanIsMatch ?? false,
          mapZenrinChibanIncludesTbn:
            freshData.mapZenrinChibanIncludesTbn ?? true,
          mapPlotRequestDetailAdditionalFields:
            freshData.mapPlotRequestDetailAdditionalFields ?? { fields: [] },
          mapInitLat: result.mapInitLat,
          mapInitLon: result.mapInitLon,
        });
      }

      toast.success("設定を変更しました");
      await mutate("/user/config/map");
    } catch (error) {
      toast.error("設定の更新に失敗しました。");
    }
  };

  return (
    <SectionPaper>
      <Box sx={{ display: "flex" }}>
        <SectionTitle>各種設定</SectionTitle>
      </Box>
      <Divider />
      <Box>
        {/* 不動産登記 */}
        <Box sx={{ m: 2 }}>
          <SubSectionTitle>不動産登記</SubSectionTitle>
          <Label>経過日数</Label>
          <Box sx={{ mb: 2 }}>
            <FormControl
              variant="outlined"
              error={Boolean(
                reactHookForm.formState.errors.pictureRequestPastDays
              )}
            >
              <InputLabel htmlFor="outlined-adornment-pastdays">
                経過日数
              </InputLabel>
              <OutlinedInput
                sx={{ width: 200 }}
                id="outlined-adornment-pastdays"
                type={"number"}
                {...reactHookForm.register("pictureRequestPastDays", {
                  valueAsNumber: true,
                })}
                error={Boolean(
                  reactHookForm.formState.errors.pictureRequestPastDays
                )}
                inputProps={{
                  min: PAST_DAYS_MIN,
                  max: PAST_DAYS_MAX,
                }}
                endAdornment={
                  <InputAdornment position="end">日</InputAdornment>
                }
                label="経過日数"
              />
              <FormHelperText
                error={Boolean(
                  reactHookForm.formState.errors.pictureRequestPastDays
                )}
              >
                {reactHookForm.formState.errors.pictureRequestPastDays?.message}
              </FormHelperText>
            </FormControl>
          </Box>
        </Box>
        {/* 地図 */}
        <Box sx={{ m: 2 }}>
          <SubSectionTitle>地図</SubSectionTitle>
          <Box sx={{ mb: 3 }}>
            <Label>初期表示地点</Label>
            <Box sx={{ mb: 2 }}>
              <FormControl variant="outlined" sx={{ display: "flex", gap: 1 }}>
                <Select
                  sx={{ width: 200 }}
                  id="initial-map-location-select"
                  displayEmpty
                  value={convertCoordinatesToStationName() ?? ""}
                  onChange={(e) => {
                    const selectedStation = INITIAL_MAP_LOCATIONS.find(
                      (location) => location.name === e.target.value
                    );
                    if (selectedStation) {
                      const mapInitSetValueOptions = {
                        shouldValidate: true,
                        shouldTouch: true,
                        shouldDirty: true,
                      };
                      reactHookForm.setValue(
                        "mapInitLat",
                        selectedStation.lat,
                        mapInitSetValueOptions
                      );
                      reactHookForm.setValue(
                        "mapInitLon",
                        selectedStation.lon,
                        mapInitSetValueOptions
                      );
                    }
                  }}
                >
                  <MenuItem value="">カスタム</MenuItem>
                  {INITIAL_MAP_LOCATIONS.map((location: MapLocation) => (
                    <MenuItem key={location.name} value={location.name}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="緯度"
                type="number"
                sx={{ width: 220 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">度</InputAdornment>
                  ),
                }}
                error={Boolean(reactHookForm.formState.errors.mapInitLat)}
                helperText={reactHookForm.formState.errors.mapInitLat?.message}
                {...reactHookForm.register("mapInitLat", {
                  valueAsNumber: true,
                })}
              />
              <TextField
                label="経度"
                type="number"
                sx={{ width: 220 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">度</InputAdornment>
                  ),
                }}
                error={Boolean(reactHookForm.formState.errors.mapInitLon)}
                helperText={reactHookForm.formState.errors.mapInitLon?.message}
                {...reactHookForm.register("mapInitLon", {
                  valueAsNumber: true,
                })}
              />
            </Box>
          </Box>
          {shouldShowMapChibanSettings && (
            <Box sx={{ mb: 3 }}>
              <Label>地番検索モード（地番・住居表示から検索する）</Label>
              <Controller
                name="mapChibanIsMatch"
                control={reactHookForm.control}
                render={({ field }) => (
                  <RadioGroup
                    row={true}
                    value={String(field.value)}
                    onChange={(_, value) => {
                      field.onChange(value === "true");
                    }}
                  >
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          あいまい一致
                          <Tooltip
                            placement="top"
                            title="地番から検索する場合、一致するものがない場合は近い場所が検索結果に表示されます"
                          >
                            <HelpIcon
                              sx={{ ml: 0.5, color: "action.active" }}
                            />
                          </Tooltip>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="完全一致"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          )}
          {hasZenrinContract && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Label>親番を検索結果に含める</Label>
                <Tooltip
                  placement="top"
                  title={
                    <Box sx={{ whiteSpace: "pre-line" }}>
                      {`ゼンリン地図検索で親番レベルまで検索範囲を拡大します。\n登記が取得できない地番が表示される場合があります。\n含めない場合は、親番のみの地番が検索されません。（例：東京都港区南青山６丁目３７６）`}
                    </Box>
                  }
                >
                  <HelpIcon sx={{ ml: 0.5, mb: 1, color: "action.active" }} />
                </Tooltip>
              </Box>
              <Controller
                name="mapZenrinChibanIncludesTbn"
                control={reactHookForm.control}
                render={({ field }) => (
                  <RadioGroup
                    row={true}
                    value={String(field.value)}
                    onChange={(_, value) => {
                      field.onChange(value === "true");
                    }}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="含める"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="含めない"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          )}
        </Box>
        {/* 地図・物件リスト */}
        {crm && (
          <Box sx={{ m: 2 }}>
            <SubSectionTitle>地図・物件リスト</SubSectionTitle>
            <SavedMapPlotRequestDetailAdditionalFieldsConfig
              value={
                reactHookForm.watch("mapPlotRequestDetailAdditionalFields") ?? {
                  fields: [],
                }
              }
              onChange={(value) => {
                reactHookForm.setValue(
                  "mapPlotRequestDetailAdditionalFields",
                  value,
                  {
                    shouldDirty: true,
                  }
                );
              }}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          mr: 2,
          my: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {reactHookForm.formState.isDirty && (
          <Box
            sx={{
              fontSize: "0.875rem",
              color: "error.main",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            ※ 変更を反映するには保存ボタンを押してください
          </Box>
        )}
        <SubmitButton
          onClick={(_event, cancelLoading) => {
            onSubmit()
              .then(() => {
                cancelLoading();
              })
              .catch((reason) => {
                cancelLoading();
                console.error(reason);
              });
          }}
          timeout={30 * 1000}
          label="保存"
          variant={ButtonVariantOption.Contained}
          size={SizingWrapperStyle.SMALL}
          loadingIndicator={"設定中..."}
        />
      </Box>
    </SectionPaper>
  );
};
